<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap>
      <v-flex xs12>
        <h1> {{ user.fullname }} </h1>
        <h3>{{ user.email }}</h3>
        <h1> 
          {{ $t('prices') }} 
          <v-btn color="primary" outline @click="goTo('users', { userFiltersStore: true })"> {{ $t('back') }} </v-btn>
        </h1>
      </v-flex>

      <!-- Tabs for Active/Suspended -->
      <v-flex xs12>
        <v-tabs slider-color="primary" centered class="elevation-1" v-model="selectedTab">
          <v-tab>
            {{ $t('list.statuses.active') }}
          </v-tab>
          <v-tab>
            {{ $t('list.statuses.suspended') }}
          </v-tab>
        </v-tabs>
      </v-flex>

      <v-flex xs12 v-if="carriers.length === 0">
        <v-card>
          <v-alert type="info" :value="true" centered v-html="$t('noDataAvailable')"></v-alert>
        </v-card>
      </v-flex>

      <v-flex xs12 v-for="carrier in carriers" :key="carrier.id" v-else>
        <v-card>
          <v-card-title>
            <h2> {{ carrier.name }} </h2>
          </v-card-title>

          <v-data-table class="elevation-1"
                        :items="carrierServices(carrier)"
                        hide-headers
                        hide-actions
                        :no-data-text="$t('noDataAvailable')">
            <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
            <template v-slot:items="props">
              <td> 
                <v-layout class="mb-2">
                  <v-flex xs12 sm6 md6>
                    <h3 class="d-inline-block">{{ props.item.name }}</h3>
                    <v-btn 
                      color="primary"
                      dark
                      @click="saveServiceAmounts(props.item.id)"
                      :loading="savingServices[props.item.id]"
                      :disabled="savingServices[props.item.id]"
                      class="ml-4"
                    >
                      {{ $t('save') }} Amounts
                    </v-btn>
                  </v-flex>

                  <v-flex xs12 sm6 md6 text-xs-right>
                    <v-btn color="primary" flat icon @click="goTo('editUserPrice', { userId: $route.params.userId, serviceId: props.item.id })">
                      <font-awesome-icon icon="edit"></font-awesome-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>

                <v-layout class="mb-4" v-if="serviceAmounts[props.item.id]" column pl-4>
                  <!-- Dev Amounts -->
                  <div v-if="$auth.isDev()">
                    <div class="font-weight-bold mb-1">{{ $t('attrs.dev_amounts') }}</div>
                    <v-layout>
                      <v-flex xs12 sm6 md3>
                        <v-text-field
                          v-model="serviceAmounts[props.item.id].international_amount_dev"
                          :label="`${ $t('attrs.amount') } base`"
                          type="number"
                          dense
                          hide-details
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 md3>
                        <v-text-field
                          v-model="serviceAmounts[props.item.id].international_percent"
                          :label="$t('percentage')"
                          type="number"
                          dense
                          hide-details
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </div>
                  <!-- Main Admin Amounts -->
                  <div v-if="$auth.isDev() || $auth.isMainAdmin()" class="pt-2">
                    <div class="font-weight-bold mb-1 mt-1">{{ $t('attrs.main_admin_amounts') }}</div>
                    <v-layout>
                      <v-flex xs12 sm6 md3>
                        <v-text-field
                          v-model="serviceAmounts[props.item.id].main_admin_amount"
                          :label="`${ $t('attrs.amount') } base`"
                          type="number"
                          dense
                          hide-details
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 md3>
                        <v-text-field
                          v-model="serviceAmounts[props.item.id].main_admin_percent"
                          :label="$t('percentage')"
                          type="number"
                          dense
                          hide-details
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </div>
                  <!-- Client Amounts -->
                  <div class="pt-2">
                    <div class="font-weight-bold mb-1 mt-1">{{ $t('attrs.client_amounts') }}</div>
                    <v-layout>
                      <v-flex xs12 sm6 md3>
                        <v-text-field
                          v-model="serviceAmounts[props.item.id].international_amount"
                          :label="$t('attrs.amount')"
                          type="number"
                          dense
                          hide-details
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 md3>
                        <v-text-field
                          v-model="serviceAmounts[props.item.id].client_percentge"
                          :label="$t('attrs.client_percentage')"
                          type="number"
                          dense
                          hide-details
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </div>
                </v-layout>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import User from '@/client/user'
import Carrier from '@/client/carrier'
import Service from '@/client/service'
import UserServiceAmount from '@/client/user-service-amount'

export default {
  name: 'UserPricesView',

  props: {},

  data () {
    return {
      user: {},
      carriers: [],
      services: [],
      serviceAmounts: {},
      savingServices: {},
      userServiceAmounts: {},
      selectedTab: 0, // 0 = active, 1 = suspended
      tabStatuses: ['active', 'suspended'],
    }
  },

  components: {
  },

  methods: {
    carrierServices (carrier) {
      return this.services.filter(o => o.carrier_id === carrier.id)
    },

    async fetchCarriersAndServices() {
      const status = this.tabStatuses[this.selectedTab]
      this.carriers = await Carrier.all({ per_page: 2000, where: { status } })
      
      if (this.carriers.length > 0) {
        const carrierIds = this.carriers.map(c => c.id)
        this.services = await Service.all({ per_page: 2000, where: {carrier_id: carrierIds} })
      }
    },

    async initializeServiceAmounts() {
      const userServiceAmountsArr = await UserServiceAmount.allForUser(this.user.id)
      this.userServiceAmounts = {}
      userServiceAmountsArr.forEach(usa => {
        this.userServiceAmounts[usa.service_id] = usa
      })
      this.services.forEach(service => {
        const usa = this.userServiceAmounts[service.id]
        this.$set(this.serviceAmounts, service.id, {
          international_amount_dev: usa ? usa.international_amount_dev : (service.international_amount_dev || ''),
          international_percent: usa ? usa.international_percent : (service.international_percent || ''),
          main_admin_amount: usa ? usa.main_admin_amount : (service.main_admin_amount || ''),
          main_admin_percent: usa ? usa.main_admin_percent : (service.main_admin_percent || ''),
          international_amount: usa ? usa.international_amount : (service.international_amount || ''),
          client_percentge: usa ? usa.client_percentge : (service.client_percentge || '')
        })
        this.$set(this.savingServices, service.id, false)
      })
    },

    async saveServiceAmounts(serviceId) {
      this.$set(this.savingServices, serviceId, true)

      try {
        const amounts = this.serviceAmounts[serviceId]
        let usa = this.userServiceAmounts[serviceId]

        usa = await UserServiceAmount.createForUser(this.user.id, {
            user_id: this.user.id,
            service_id: serviceId,
            ...amounts
          })
        this.userServiceAmounts[serviceId] = usa

        this.setNotification({
          color: 'success',
          message: this.$t('successfullySavedNotification')
        })
      } catch (err) {
        this.handleErr(err)
      } finally {
        this.$set(this.savingServices, serviceId, false)
      }
    }
  },

  watch: {
    selectedTab: {
      immediate: false,
      handler: async function() {
        await this.fetchCarriersAndServices()
        await this.initializeServiceAmounts()
      }
    }
  },

  async mounted () {
    this.user = await User.findOne(this.$route.params.userId)
    await this.fetchCarriersAndServices()
    await this.initializeServiceAmounts()
  }
}
</script>
